<template>
  <ViewContainer :title="routineDefinition.name">
    <template v-slot:prepend>
      <v-btn
        class="me-1"
        color="blue"
        text
        icon
        :ripple="false"
        @click="handleBack"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
    </template>
    <template slot="header-right" v-if="canEditRoutine">
      <v-btn
        color="blue-grey darken-2"
        data-tourid="routine-title-edit"
        icon
        small
        @click="goToEditView"
      >
        <v-icon size="16">
          mdi-pencil-outline
        </v-icon>
      </v-btn>
    </template>
    <template slot="append">
      <div class="d-flex justify-end">
        <v-btn
          v-if="routineDefinition.checklist"
          rounded
          text
          color="blue"
          class="me-2"
          @click="showChecklist"
        >
          <v-icon class="me-2" size="16">
            mdi-list-status
          </v-icon>
          {{ $t('checklist.view_checklist') }}
        </v-btn>
        <v-btn rounded text color="blue" @click="handleShareQR" class="me-2">
          <v-icon class="me-2" size="16">
            mdi-qrcode
          </v-icon>
          {{ $t('routines.qr.qr_code') }}
        </v-btn>
        <v-btn
          v-if="canStartRoutine"
          rounded
          color="blue"
          class="white--text"
          @click="handleStart"
        >
          <v-icon class="me-2" size="16">
            mdi-play
          </v-icon>
          {{ $t('routines.start_routine') }}
        </v-btn>
      </div>
    </template>
    <RoutineDetailsCard
      class="mb-4"
      :loading="loading"
      :hasRoutineExecutions="Boolean(totalExecutions)"
      :routineDefinition="routineDefinition"
      @delete="deleteRoutine"
    />
    <RoutineDetailsTable
      :loadingExecutions="loadingExecutions"
      :loadingFindings="loadingFindings"
      :executions="executions"
      :totalExecutions="totalExecutions"
      :findings="findings"
      :totalFindings="totalFindings"
      :routineDefinition="routineDefinition"
      @getRoutineExecutions="getRoutineExecutions"
      @getRoutineFindings="getRoutineFindings"
    />
    <ChecklistDetailDialog
      :show="showChecklistDialog"
      :checklist="checklist"
      @close="showChecklistDialog = false"
    />
    <RoutineStartModal
      :open="showStartRoutineDialog"
      :routine="routineDefinition"
      @cancel="showStartRoutineDialog = false"
      @started="
        navigateTo({
          name: 'routineExecutionDetail',
          params: { routineId: $event },
        })
      "
    />
    <QRCodeModal
      data-testid="routineDetails-modal-qrModal"
      :routineDefinition="routineToShare"
      :open="isSharingQR"
      @close="isSharingQR = false"
    />
  </ViewContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import _ from 'lodash'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import routineDefinitionPermissions from '@/mixins/routineDefinitionPermissions.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import RoutineDetailsTable from './Table/RoutineDetailsTable.vue'
import RoutineDetailsCard from './RoutineDetailsCard.vue'
import ChecklistDetailDialog from '@/components/ChecklistDetailDialog.vue'
import RoutineStartModal from '@/components/RoutineStartModal.vue'
import QRCodeModal from '@/containers/RoutineDefinitionsView/QRCodeModal.vue'

const sortOrder = {
  ASCENDING: 'asc',
  DESCENDING: 'desc',
}

const DEFAULT_SIZE = 10

export default {
  name: 'RoutineDefinitionView',

  mixins: [mixin, trackerMixin, routineDefinitionPermissions],

  components: {
    ViewContainer,
    ChecklistDetailDialog,
    RoutineStartModal,
    QRCodeModal,
    RoutineDetailsCard,
    RoutineDetailsTable,
  },

  async mounted() {
    await this.fetchRoutineDefinition()

    if (this.routineDefinition.isDeleted) return

    if (this.routineDefinition.checklist) await this.getChecklist()
  },

  data() {
    return {
      routineDefinition: {},
      checklist: {},
      routineToShare: {},
      isSharingQR: false,
      showStartRoutineDialog: false,
      showChecklistDialog: false,
      loading: true,
      loadingExecutions: true,
      loadingFindings: true,
      executions: [],
      findings: [],
      totalExecutions: 0,
      totalFindings: 0,
      isFirstLoadFindings: true,
      isFirstLoadExecutions: true,
      prevRoute: null,
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },

  computed: {
    ...mapGetters({
      accountOrganizationId: 'organizationId',
      account: 'account',
    }),

    canStartRoutine() {
      return (
        this.routineDefinition.isActive &&
        this.getRoutineDefinitionPermissions({
          routineDefinition: this.routineDefinition,
        }).start
      )
    },

    canEditRoutine() {
      return this.getRoutineDefinitionPermissions({
        routineDefinition: this.routineDefinition,
      }).edit
    },

    routineDefinitionId() {
      return this.$route?.params?.routineId
    },

    organizationId() {
      // Rever admins can manually set organizationId in the route params for testing purposes
      if (!this.account.isAdmin) return this.accountOrganizationId

      return this.$route?.params?.organizationId || this.accountOrganizationId
    },
  },

  methods: {
    goToEditView() {
      this.trackRoutineViewNavigate({
        action: 'goToEditView',
        id: this.routineDefinition?.id,
        source: 'routineDetailsTitle',
      })

      this.navigateTo({
        name: this.account.isAdmin ? 'reverAdminEditRoutine' : 'editRoutine',
        params: this.account.isAdmin
          ? {
              organizationId:
                this.$route?.params?.organizationId ||
                this.accountOrganizationId,
              routineDefinitionId: this.routineDefinitionId,
            }
          : { routineDefinitionId: this.routineDefinitionId },
      })
    },

    async getRoutineFindings(options) {
      try {
        this.loadingFindings = true
        const ITEMS_PER_PAGE = options?.itemsPerPage ?? DEFAULT_SIZE
        const response = await api.findings.getAll({
          organizationId: this.organizationId,
          filters: {
            routineDefinition: this.routineDefinitionId,
          },
          limit: ITEMS_PER_PAGE,
          page: options?.page ?? 1,
          sort: {
            by: options?.sortBy ?? 'createdAt',
            order: options?.sortDesc
              ? sortOrder.DESCENDING
              : sortOrder.ASCENDING,
          },
        })

        this.findings = response?.data?.results ?? []

        this.totalFindings = response?.data?.totalRecordsInData ?? 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loadingFindings = false
      }
    },

    async getRoutineExecutions(options) {
      try {
        this.loadingExecutions = true
        const ITEMS_PER_PAGE = options?.itemsPerPage ?? DEFAULT_SIZE
        const response = await api.routine.getRoutineExecutions(
          this.organizationId,
          {
            filters: {
              routineDefinition: this.routineDefinitionId,
            },
            limit: ITEMS_PER_PAGE,
            page: options?.page ?? 1,
            sort: {
              by: options?.sortBy ?? 'createdAt',
              order: options?.sortDesc
                ? sortOrder.DESCENDING
                : sortOrder.ASCENDING,
            },
          }
        )

        this.executions = response?.data?.results ?? []

        this.totalExecutions = response?.data?.totalRecordsInData ?? 0
        this.loadingExecutions = false
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loadingExecutions = false
      }
    },

    handleShareQR() {
      this.trackRoutineQrView({
        source: 'routineDetail',
      })
      this.isSharingQR = true
      this.routineToShare = _.clone(this.routineDefinition)
    },

    async deleteRoutine() {
      try {
        await api.routine.delete(this.organizationId, this.routineDefinition.id)
        this.navigateTo({ name: 'routineDefinitions' })
        this.showSuccess(this.$t('routines.routine_deleted_success'))
        this.trackRoutineDelete({
          action: 'delete',
          id: this.routineDefinition.id,
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async getChecklist() {
      try {
        if (!this.routineDefinitionId) return
        const response = await api.routine.getChecklistById(
          this.organizationId,
          this.routineDefinition.checklist
        )
        this.checklist = response.data
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async fetchRoutineDefinition() {
      if (!this.routineDefinitionId) return
      try {
        this.loading = true
        const response = await api.routine.getById(
          this.organizationId,
          this.routineDefinitionId
        )
        this.routineDefinition = response?.data ?? {}

        if (this.routineDefinition.isDeleted) {
          return this.navigateTo('/404')
        }
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    showChecklist() {
      this.trackRoutineViewNavigate({
        action: 'showChecklist',
        source: 'routineDetail',
        id: this.routineDefinitionId,
      })
      this.showChecklistDialog = true
    },

    handleStart() {
      this.trackRoutineViewNavigate({
        action: 'goToStart',
        source: 'routineDetail',
        id: this.routineDefinitionId,
      })
      this.showStartRoutineDialog = true
    },

    handleBack() {
      this.trackRoutineViewNavigate({
        action: 'goBack',
        source: 'routineDetail',
      })
      if (this.prevRoute?.name) {
        this.$router.back()
      } else {
        this.navigateTo({
          name: 'routineDefinitions',
        })
      }
    },
  },
}
</script>
<style lang="scss">
.routine-global-chip {
  border: 1px solid $color-blue-lighten1 !important;
  background-color: $color-blue-lighten2 !important;
}
</style>
